"use client";

import React from "react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Separator } from "@/components/ui/separator";
import { Card, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";


function ArtistContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="container mx-auto px-4 animate-pulse">
        <div className="w-full h-[400px] bg-muted rounded-lg mb-8"></div>
        <div className="h-12 bg-muted w-1/2 mx-auto rounded-md mb-4"></div>
        <div className="h-6 bg-muted w-1/3 mx-auto rounded-md mb-12"></div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2 space-y-4">
            <div className="h-4 bg-muted w-full rounded-md"></div>
            <div className="h-4 bg-muted w-full rounded-md"></div>
            <div className="h-4 bg-muted w-3/4 rounded-md"></div>
          </div>
          <div className="h-48 bg-muted rounded-lg"></div>
        </div>
      </div>
    );
  }

  if (error || posts.length === 0) {
    return (
      <div className="container mx-auto px-4 text-center py-24">
        <h2 className="font-default text-2xl font-semibold text-muted-foreground">Talent profile not found</h2>
      </div>
    );
  }

  const post = posts[0];
  const customFields = post.customFields || {};

  const name = customFields.artist_name || post.title;
  const roles = customFields.artist_roles;
  const bio = customFields.biography;
  const bookingFocus = customFields.booking_focus;
  const image = post.featuredImage || customFields.artist_image;

  return (
    <div className="container mx-auto px-4">
      {}
      {image && (
        <div className="mb-12 overflow-hidden rounded-lg shadow-xl border border-primary/20">
          <AspectRatio ratio={21 / 9}>
            <img
              src={image}
              alt={name}
              className="object-cover w-full h-full"
            />
          </AspectRatio>
        </div>
      )}

      {}
      <div className="text-center max-w-4xl mx-auto mb-16">
        <h1 className="font-default text-5xl md:text-7xl font-bold tracking-tighter text-primary mb-6 uppercase">
          {name}
        </h1>
        {roles && (
          <p className="text-xl md:text-2xl text-accent font-medium uppercase tracking-widest">
            {roles}
          </p>
        )}
      </div>

      <Separator className="my-16 bg-primary/30" />

      {}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-24">
        {}
        <div className="lg:col-span-8 space-y-8">
          {bio && (
            <section>
              <h2 className="font-default text-3xl font-semibold mb-6 text-foreground border-l-4 border-primary pl-6">The Journey</h2>
              <div className="prose prose-lg dark:prose-invert max-w-none whitespace-pre-wrap text-muted-foreground leading-relaxed font-default">
                {bio}
              </div>
            </section>
          )}
        </div>

        {}
        <div className="lg:col-span-4 space-y-8">
          {bookingFocus && (
            <section className="bg-card p-8 rounded-lg border border-primary shadow-lg sticky top-24">
              <h2 className="font-default text-2xl font-semibold mb-6 text-primary">Performance Focus</h2>
              <div className="whitespace-pre-wrap text-muted-foreground leading-relaxed font-default">
                {bookingFocus}
              </div>
              <div className="mt-8">
                <Link to="/SecureYourTalentPage" className="inline-block w-full py-4 bg-primary text-primary-foreground text-center font-bold rounded-md hover:bg-accent transition-colors uppercase tracking-wider">
                  Book This Artist
                </Link>
              </div>
            </section>
          )}
        </div>
      </div>

      {}
      <RelatedArtists />
    </div>
  );
}


function RelatedArtistsCarousel() {
  const { posts, loading } = usePosts();

  if (loading) {
    return (
      <div className="flex gap-4 overflow-hidden">
        {[1, 2, 3].map((i) => (
          <div key={i} className="w-full md:w-1/3 h-64 bg-muted rounded-lg animate-pulse shrink-0"></div>
        ))}
      </div>
    );
  }

  if (posts.length === 0) return null;

  return (
    <Carousel
      opts={{
        align: "start",
        loop: true,
      }}
      className="w-full"
    >
      <CarouselContent className="-ml-4">
        {posts.map((post, i) => (
          <CarouselItem key={post.id} data-index={i} className="pl-4 md:basis-1/2 lg:basis-1/3">
            <Link to={post.link || "#"} className="block h-full group">
              <Card className="h-full overflow-hidden border-primary/20 shadow-md group-hover:shadow-primary/20 group-hover:border-primary transition-all duration-300 bg-card">
                {post.featuredImage ? (
                  <AspectRatio ratio={4 / 3} className="overflow-hidden">
                    <img
                      src={post.featuredImage}
                      alt={post.title}
                      className="object-cover w-full h-full group-hover:scale-110 transition-transform duration-700"
                    />
                  </AspectRatio>
                ) : (
                  <AspectRatio ratio={4 / 3} className="bg-muted flex items-center justify-center">
                    <span className="text-muted-foreground font-default">Portfolio Image</span>
                  </AspectRatio>
                )}
                <CardHeader className="bg-card">
                  <CardTitle className="font-default text-xl group-hover:text-primary transition-colors uppercase tracking-tight">
                    {post.customFields?.artist_name || post.title}
                  </CardTitle>
                  {post.customFields?.artist_roles && (
                    <CardDescription className="line-clamp-2 text-accent/80 font-default">
                      {post.customFields.artist_roles}
                    </CardDescription>
                  )}
                </CardHeader>
              </Card>
            </Link>
          </CarouselItem>
        ))}
      </CarouselContent>
      <div className="flex items-center justify-center gap-4 mt-8">
        <CarouselPrevious className="static translate-y-0 border-primary text-primary hover:bg-primary hover:text-primary-foreground" />
        <CarouselNext className="static translate-y-0 border-primary text-primary hover:bg-primary hover:text-primary-foreground" />
      </div>
    </Carousel>
  );
}


function RelatedArtists() {
  
  const wp_query = new WP_Query({ post_type: "artist", per_page: 6 });

  return (
    <section className="pt-16 border-t border-primary/30">
      <div className="flex flex-col md:flex-row justify-between items-end mb-10 gap-4">
        <div>
          <h2 className="font-default text-4xl font-bold text-foreground uppercase tracking-tighter">World-Class Roster</h2>
          <p className="text-muted-foreground mt-2 font-default">Explore more stars from International Experiences agency.</p>
        </div>
        <Link to="/RosterPage" className="text-primary hover:text-accent font-bold uppercase tracking-widest text-sm transition-colors">
          View Full Roster →
        </Link>
      </div>
      <PostsProvider wp_query={wp_query}>
        <RelatedArtistsCarousel />
      </PostsProvider>
    </section>
  );
}


export default function SingleArtistTemplateSection() {
  
  
  const currentWPQueryParams = typeof wvcClient !== 'undefined' ? wvcClient.getCurrentWPQueryParams() : { post_type: 'artist' };
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section data-section-id="254166" id="single-artist-template-section" className="bg-background text-foreground py-16 md:py-24">
      <PostsProvider wp_query={wp_query}>
        <ArtistContent />
      </PostsProvider>
    </section>
  );
}